<script setup>
import {computed, getCurrentInstance, onMounted, reactive, ref, watch} from "vue";
import {datePipe} from "@/utils/datatimeUtils";
import {
  accessControlApi,
  articlesApi,
  categories_Api, connectionsApi,
  futured_medias_Api,
  prompt_templates_Api,
  sample_articles_Api
} from "@/api/forwriting_index";
import CheckPointLimit from "@/views/forwriting/fw00_check_point_limit.vue"

import {useRoute, useRouter} from "vue-router";
import LoadingIcon from "@/components/LoadingIcon.vue";
import Paginate from "vuejs-paginate-next"

// for locale
const instance = getCurrentInstance()
const t = instance.proxy.$t

const router = useRouter();
const route = useRoute()
const article_list_id = parseInt(route.params.article_list_id) || null;

// 設定持久化相關函數 - 為每個 article_list 保存獨立設定
const getStorageKey = (listId, key) => `article_list_${listId || 'default'}_${key}`;

// 載入設定
const loadSettingsFromStorage = (listId) => {
  if (!listId) {
    return {
      page: 1,
      sort_by: "created_at",
      sort_order: "desc",
      filter_article_generate_status: -1,
      filter_cms_post_status: -1,
      search_key: "",
      article_defaults: {
        generate_type_id: 3,
        ai_model_id: 6,
        image_generation_mode: false,
        image_style_setting: "",
        sample_article_id: null,
        prompt_template_id: null,
        futured_media_id: null,
        connection_id: null
      }
    };
  }
  
  const settings = {
    page: parseInt(localStorage.getItem(getStorageKey(listId, 'page')) || '1', 10),
    sort_by: localStorage.getItem(getStorageKey(listId, 'sort_by')) || "created_at",
    sort_order: localStorage.getItem(getStorageKey(listId, 'sort_order')) || "desc",
      filter_article_generate_status: parseInt(localStorage.getItem(getStorageKey(listId, 'filter_article_generate_status')) || '-1', 10),
      filter_cms_post_status: parseInt(localStorage.getItem(getStorageKey(listId, 'filter_cms_post_status')) || '-1', 10),
      search_key: localStorage.getItem(getStorageKey(listId, 'search_key')) || "",
      article_defaults: {
      generate_type_id: parseInt(localStorage.getItem(getStorageKey(listId, 'default_generate_type_id')) || '3', 10),
      ai_model_id: parseInt(localStorage.getItem(getStorageKey(listId, 'default_ai_model_id')) || '6', 10),
      image_generation_mode: localStorage.getItem(getStorageKey(listId, 'default_image_generation_mode')) === 'true',
      image_style_setting: localStorage.getItem(getStorageKey(listId, 'default_image_style_setting')) || "",
      sample_article_id: localStorage.getItem(getStorageKey(listId, 'default_sample_article_id')) || null,
      prompt_template_id: localStorage.getItem(getStorageKey(listId, 'default_prompt_template_id')) || null,
      futured_media_id: localStorage.getItem(getStorageKey(listId, 'default_futured_media_id')) || null,
      connection_id: localStorage.getItem(getStorageKey(listId, 'default_connection_id')) || null
    }
  };
  
  // 處理 null 字符串
  if (settings.article_defaults.sample_article_id === 'null') settings.article_defaults.sample_article_id = null;
  if (settings.article_defaults.prompt_template_id === 'null') settings.article_defaults.prompt_template_id = null;
  if (settings.article_defaults.futured_media_id === 'null') settings.article_defaults.futured_media_id = null;
  if (settings.article_defaults.connection_id === 'null') settings.article_defaults.connection_id = null;
  
  return settings;
};

// 保存設定
const saveSettingsToStorage = (listId, settings) => {
  if (!listId) return;
  
  if (settings.page !== undefined) {
    localStorage.setItem(getStorageKey(listId, 'page'), settings.page.toString());
  }
  if (settings.sort_by !== undefined) {
    localStorage.setItem(getStorageKey(listId, 'sort_by'), settings.sort_by);
  }
  if (settings.sort_order !== undefined) {
    localStorage.setItem(getStorageKey(listId, 'sort_order'), settings.sort_order);
  }
  if (settings.filter_article_generate_status !== undefined) {
    localStorage.setItem(getStorageKey(listId, 'filter_article_generate_status'), settings.filter_article_generate_status.toString());
  }
  if (settings.filter_cms_post_status !== undefined) {
    localStorage.setItem(getStorageKey(listId, 'filter_cms_post_status'), settings.filter_cms_post_status.toString());
  }
  if (settings.search_key !== undefined) {
    localStorage.setItem(getStorageKey(listId, 'search_key'), settings.search_key);
  }
  if (settings.article_defaults) {
    const defaults = settings.article_defaults;
    if (defaults.generate_type_id !== undefined) {
      localStorage.setItem(getStorageKey(listId, 'default_generate_type_id'), defaults.generate_type_id.toString());
    }
    if (defaults.ai_model_id !== undefined) {
      localStorage.setItem(getStorageKey(listId, 'default_ai_model_id'), defaults.ai_model_id.toString());
    }
    if (defaults.image_generation_mode !== undefined) {
      localStorage.setItem(getStorageKey(listId, 'default_image_generation_mode'), defaults.image_generation_mode.toString());
    }
    if (defaults.image_style_setting !== undefined) {
      localStorage.setItem(getStorageKey(listId, 'default_image_style_setting'), defaults.image_style_setting);
    }
    if (defaults.sample_article_id !== undefined) {
      localStorage.setItem(getStorageKey(listId, 'default_sample_article_id'), defaults.sample_article_id || 'null');
    }
    if (defaults.prompt_template_id !== undefined) {
      localStorage.setItem(getStorageKey(listId, 'default_prompt_template_id'), defaults.prompt_template_id || 'null');
    }
    if (defaults.futured_media_id !== undefined) {
      localStorage.setItem(getStorageKey(listId, 'default_futured_media_id'), defaults.futured_media_id || 'null');
    }
    if (defaults.connection_id !== undefined) {
      localStorage.setItem(getStorageKey(listId, 'default_connection_id'), defaults.connection_id || 'null');
    }
  }
};

// 載入當前 article_list 的設定
const loadCurrentSettings = () => {
  const settings = loadSettingsFromStorage(article_list_id);
  return settings;
};

// 載入當前設定（需要在所有使用 currentSettings 的地方之前定義）
const currentSettings = loadCurrentSettings();

console.log( "article_list_id:", article_list_id)

// to show the modal_article_new by url
const is_new = route.query.is_new
console.log( "is_new:", is_new)

// const authentication_types = [
//   "Basic Authentication",
//   "JWT Authentication",
//   "OAuth 2.0 Authentication",
//   "Application Passwords Authentication"
// ]

const show_modal_article_new = ref(false);

// URL/テキスト 自動判定（冒頭が http:// / https:// で始まる場合のみURLモード）
const isUrlMode = computed(() => {
  const v = (data.article.urls || '').trim()
  if (!v) return true  // 空欄はURLモードをデフォルト表示
  return /^https?:\/\//i.test(v)
})

// 一次ソース判定
const primarySourceCheck = reactive({
  loading: false,
  results: [],
  checked: false
})

const check_primary_source = async () => {
  if (!data.article.urls || !data.article.urls.trim()) {
    alert(t('Articles_Edit.primary_source_no_urls'))
    return
  }
  primarySourceCheck.loading = true
  primarySourceCheck.results = []
  primarySourceCheck.checked = false
  try {
    const resp = await articlesApi.check_primary_source(data.article.urls)
    if (resp.status === 200 && resp.data && resp.data.results) {
      primarySourceCheck.results = resp.data.results
      primarySourceCheck.checked = true
    }
  } catch (error) {
    console.error("Primary source check error:", error)
    alert(t('Articles_Edit.primary_source_error'))
  } finally {
    primarySourceCheck.loading = false
  }
}

const open_articles_new = async () =>
{
  console.log( "article_list_id:", article_list_id)
  console.log( "article(open_articles_new):", data.article)

  if( open_check_modal())
    return

  isLoading.value = true

  const articleData = { ...data.article }
  if (articleData.futured_media_id === "0" || articleData.futured_media_id === "" || articleData.futured_media_id === undefined) {
    articleData.futured_media_id = 0
  } else if (articleData.futured_media_id === "-1") {
    articleData.futured_media_id = -1
  }

  // 新增一筆 article
  let resp = await articlesApi.create( article_list_id, articleData)
  console.log( "resp:", resp)

  // 取得 article_id
  let article_id = resp.data.id
  console.log( "article_id:", article_id)

  // 多個選擇的 category_id
  let category_ids = Object.keys( categories_selected.value)
  console.log( "category_ids:", category_ids)

  // 加入一對多的 article_id -> category_id 對應關係

  const formData = new FormData()
  if( category_ids.length === 0)
    formData.append( "category_ids", "0")
  else
    formData.append( "category_ids", category_ids)

  console.log( "formData:", formData)
  let resp_1 =  await articlesApi.create_article_categories( article_id, formData)
  console.log( "resp_1:", resp_1)

  // 音声データから生成の場合: 補足情報保存 + 音声ファイルアップロード
  const generate_type_id_val = parseInt(data.article.generate_type_id)
  if (generate_type_id_val === 5) {
    if (audio_supplement.value) {
      await articlesApi.update(article_id, { audio_supplement: audio_supplement.value })
    }
    if (audio_file.value) {
      audio_upload_status.value = "uploading"
      try {
        await articlesApi.upload_audio(article_id, audio_file.value)
        audio_upload_status.value = "done"
        console.log("✅ audio uploaded for article_id:", article_id)
      } catch (e) {
        audio_upload_status.value = "error"
        console.error("audio upload error:", e)
        isLoading.value = false
        return
      }
    }
  }

  // インライン画像アップロード
  if (inline_image_files.value.length > 0 && generate_type_id_val !== 4) {
    try {
      await articlesApi.upload_images(article_id, inline_image_files.value)
      console.log("✅ inline images uploaded for article_id:", article_id)
    } catch (e) {
      console.error("inline image upload error:", e)
    }
  }

  // 關閉 modal 自己
  show_modal_article_new.value = false

  // 記事生成を開始
  try {
    await articlesApi.generate(article_id)
    console.log("✅ generate queued for article_id:", article_id)
  } catch (e) {
    console.error("generate error:", e)
  }

  // open the articles edit page
  let url = `/forwriting/article_lists/${article_list_id}/articles/${article_id}`
  console.log( "** url:", url)
  await router.push( url)
}

// ==== for modal delete ====

const show_modal_delete = ref(false);
const delete_modal_msg = ref( "")
const id_for_delete = ref(-1);
const handleDelete = async ( par_id, article_title) => {
  console.log( "*** par_id:", par_id)
  console.log( "*** article_title:", article_title)

  delete_modal_msg.value = t('Articles.delete_prompt'
  ).replace( "title", article_title)

  id_for_delete.value = par_id
  show_modal_delete.value = true;
};
const handleDelete_confirm = async () => {
  isLoading.value = true;

  const resp = await articlesApi.delete( id_for_delete.value );
  if( resp.status !== 200) {
    isLoading.value = false;
    throw new Error("ERROR");
  }

  data.items = data.items.filter( ( item) => item.id !== id_for_delete.value);

  isLoading.value = false;
  show_modal_delete.value = false;
};

// ==== for modal edit ====
const handleEdit = async ( article_id) => {
  console.log( "*** article_id:", article_id)
  const routeData = router.resolve(`/forwriting/article_lists/${article_list_id}/articles/${article_id}`);
  window.open(routeData.href, "_blank", "noopener,noreferrer");
}

// ==== for modal check ====
const modal_check = reactive({
  "show": false,
  "msg": "modal_check"
})
function is_empty( val) {
  if( val == null) return true
  if( typeof val === "string" && val.trim().length === 0) return true
  return false
}
function has_empty_fields() {
  console.log( "generate_type_id:", data.article.generate_type_id)

  let f_list = ""

  const generate_type_id = parseInt( data.article.generate_type_id)
  if( generate_type_id === 3 || generate_type_id === 4) {
    if( is_empty( data.article.urls))
      f_list += t('Articles.urls') + ", "
  }
  if( generate_type_id === 5) {
    if( !audio_file.value)
      f_list += t('Articles.audio_file') + ", "
  }
  if( generate_type_id === 6) {
    if( is_empty( data.article.additional_prompt))
      f_list += t('Articles.agent_instruction') + ", "
  }

  if( generate_type_id !== 6) {
    if( is_empty( data.article.sample_article_id))
      f_list += t('Articles.sample_article_name') + ", "
    if( is_empty( data.article.prompt_template_id))
      f_list += t('Articles.prompt_template_name') + ", "
  }

  // has some empty fields...
  if( f_list.length > 0) {
    modal_check.msg = f_list.substring( 0, f_list.length-2)
        + t( 'Articles.check_modal_msg')
    return true
  }

  return false
}
const open_check_modal = () => {
  let has_empty = has_empty_fields()
  if( has_empty) {
    modal_check.show = true
  }
  return has_empty
}

// ==== for article_generate_status ====
/** 列表狀態標籤：依目前語系（tw/ja/en）顯示，勿硬編日文 */
const labelArticleGenerateStatus = (s) => t(`Articles.article_generate_status_${s}`)
const article_generate_status_color = {
  0: "rgb( 197, 197, 197)",
  1: "rgb( 104, 151, 231)",
  2: "rgb(   0, 255, 113)",
  3: "rgb( 255,  37, 4)"
}

// 生成タイプバッジ色
const generate_type_color = {
  1: "#6c757d",   // AI生成
  2: "#6c757d",   // その他
  3: "#6c757d",   // URL転載
  4: "#6c757d",   // 画像転載
}
/** 列表「生成類型」：3/4/5 用 i18n；其餘沿用 API（DB）名稱 */
const labelGenerateTypeForRow = (id) => {
  const n = parseInt(id, 10)
  if (n === 3) return t("Articles.generate_type_3")
  if (n === 4) return t("Articles.generate_type_4")
  if (n === 5) return t("Articles.generate_type_5")
  return data.generate_types[n] || ""
}
const onChange_article_generate_status = async( ) => {
  // 👉 FIX: 先重置頁面，再重新載入數據，確保過濾條件正確應用
  data.current_page = 1
  await reload_data()
}

const generate_type_changed = async( ) => {
  console.log( "generate_type_id:", data.article.generate_type_id)
  const generate_type_id = parseInt(data.article.generate_type_id)
  if (generate_type_id === 4) {
    data.article.image_generation_mode = false
    data.article.image_style_setting = ""
    inline_image_files.value = []
    inline_image_names.value = []
  }
  // 音声モード切り替え時は音声ファイルをリセット
  if (generate_type_id !== 5) {
    audio_file.value = null
    audio_file_name.value = ""
    audio_supplement.value = ""
  }
  // テーマから生成モード: URLをクリア
  if (generate_type_id === 6) {
    data.article.urls = ""
  }
}

// ==== 音声ファイルアップロード (generate_type_id=5) ====
const audio_file         = ref(null)
const audio_file_name    = ref("")
const audio_supplement   = ref("")
const audio_upload_status = ref("") // "" | "uploading" | "done" | "error"

const on_audio_file_change = (event) => {
  const file = event.target.files[0]
  if (!file) return
  audio_file.value = file
  audio_file_name.value = file.name
  audio_upload_status.value = ""
}

// ==== 記事内画像アップロード ====
const inline_image_files = ref([])
const inline_image_names = ref([])

const on_inline_image_change = (event) => {
  const files = Array.from(event.target.files)
  inline_image_files.value = [...inline_image_files.value, ...files]
  inline_image_names.value = inline_image_files.value.map(f => f.name)
  // ファイル入力をリセット（同じファイルを再選択可能に）
  event.target.value = ""
}
const remove_inline_image = (index) => {
  inline_image_files.value.splice(index, 1)
  inline_image_names.value = inline_image_files.value.map(f => f.name)
}

// ==== for cms_post ====
const labelCmsPostStatus = (s) => t(`Articles.cms_post_status_${s}`)
const cms_post_status_color = {
  0: "rgb( 197, 197, 197)",
  1: "rgb( 104, 151, 231)",
  2: "rgb(   0, 255, 113)",
  3: "rgb( 255,  37, 4)"
}
const onChange_cms_post_status = async( ) => {
  // 👉 FIX: 先重置頁面，再重新載入數據，確保過濾條件正確應用
  data.current_page = 1
  await reload_data()
}

// ==== for filter ====
const filterKey = reactive({
  searchKey: null,
});
const searchKey = ref(currentSettings.search_key || "");
const handleFilter = async () => {
  filterKey.searchKey = searchKey.value;
  data.current_page = 1
  // 保存搜索關鍵字
  if (article_list_id) {
    saveSettingsToStorage(article_list_id, { search_key: searchKey.value });
  }
  // 👉 ADD: Reload data when filter is applied to get search results from backend
  await reload_data()
};
const filteredData = computed(() =>
{
  // 👉 CHANGE: 所有過濾（關鍵字和狀態）現在都由後端處理
  // 返回的 data.items 已經是過濾後的結果，直接返回即可
  // 👉 FIX: Ensure data.items is always an array
  if (!data.items || !Array.isArray(data.items)) {
    return [];
  }
  return data.items;
});

// 👉 ADD: Computed property to determine if we're currently searching
const isSearching = computed(() => {
  // 👉 FIX: Add null check to prevent errors
  if (!filterKey || !filterKey.searchKey) {
    return false;
  }
  const searchKey = filterKey.searchKey;
  return typeof searchKey === 'string' && searchKey.trim() !== '';
});

const PAGE_NUM_OPTIONS = [10, 25, 50, 100]

const handlePageNumChange = async () => {
  data.current_page = 1
  await reload_data()
}

// ==== for Paginate ====
// 👉 REMOVED: clickCallback is no longer needed, watch() handles page changes
const page_count = computed(() => {
  // 👉 CHANGE: All queries (including search and status filters) now use pagination
  // Use total_count from backend
  // 👉 FIX: Add safety checks to prevent null errors
  if (!data) {
    return 1
  }
  const total = data.total_count || 0
  const pageNum = data.page_num || 25
  if (total === 0 || pageNum === 0) {
    return 1
  }
  let cc = Math.floor( total / pageNum)
  if( total % pageNum === 0)
    return cc
  return cc + 1
})

// ==== the main data ====
const data = reactive({
  article: {
    "id": 1,

    "posted_url": "",
    "article_title": "",
    "keyword": "",
    "urls": "",
    "subtitles": "",
    "slug": "",
    "tags": "",
    "content": "",
    "word_count": 0,

    "article_generate_status": 0,
    "cms_post_status": 0,

    "additional_prompt": "",

    "article_list_id": null,
    "generate_type_id": currentSettings.article_defaults.generate_type_id,
    "sample_article_id": currentSettings.article_defaults.sample_article_id,
    "prompt_template_id": currentSettings.article_defaults.prompt_template_id,
    "futured_media_id": currentSettings.article_defaults.futured_media_id,
    "connection_id": currentSettings.article_defaults.connection_id,
    "ai_model_id": currentSettings.article_defaults.ai_model_id,
    "image_generation_mode": currentSettings.article_defaults.image_generation_mode,
    "image_style_setting": currentSettings.article_defaults.image_style_setting
  },

  current_page: currentSettings.page,
  page_num: 25,
  total_count: 0,  // 👉 ADD: total count from backend

  items: [],
  checked_items: {},

  // generate_type_selected: 0,

  generate_types: {
    1: '長文生成(キーワードから)',     // 保留以支持舊文章顯示
    2: '長文生成(URL & 見出しから)',  // 保留以支持舊文章顯示
    3: '元ネタから生成(URLから)',  // 預設值，確保立即顯示
    4: '元ネタから生成(画像転載あり)'  // 画像転載モード
  },
  // 1: "長文生成(キーワードから)",     // 長文本生成（來自關鍵字）
  // 2: "長文生成(URL & 見出しから)",  // 長文本生成（來自 URL 和標題）
  // 3: "元ネタから生成(URLから)",   // 新聞轉載產生（來自 URL）
  // 4: "元ネタから生成(画像転載あり)" // 新聞轉載 Generation_image 包含（來自 URL）

  // for modal select/options
  sample_articles: {},
  prompt_templates: {},
  prompt_templates_content: {},
  categories: {},
  futured_medias: {},
  connections: {},
  connections_data: {}, // 存儲連接的完整信息（包括 integration）
  ai_models: {},

  filter_article_generate_status: currentSettings.filter_article_generate_status,
  filter_cms_post_status: currentSettings.filter_cms_post_status,
  
  // 排序相關
  sort_by: currentSettings.sort_by,
  sort_order: currentSettings.sort_order
})
const reload_data = async( ) => {
  const currentArticleListId = parseInt(route.params.article_list_id) || null;
  if (!currentArticleListId) {
    console.error("article_list_id is missing:", route.params);
    return;
  }
  // 👉 ADD: Pass search_keyword to backend API
  const search_keyword = filterKey.searchKey && filterKey.searchKey.trim() ? filterKey.searchKey.trim() : null
  
  // 👉 ADD: Pass status filters to backend API
  const article_generate_status = data.filter_article_generate_status >= 0 ? data.filter_article_generate_status : null
  const cms_post_status = data.filter_cms_post_status >= 0 ? data.filter_cms_post_status : null
  
  console.log( "*** reload_data - sort_by:", data.sort_by, "sort_order:", data.sort_order, "page:", data.current_page, "search_keyword:", search_keyword, "article_generate_status:", article_generate_status, "cms_post_status:", cms_post_status)
  
  // 👉 CHANGE: All queries (including search and status filters) now use normal pagination
  const limit = data.page_num
  const offset = (data.current_page - 1) * limit
  console.log( "📄 PAGINATION MODE: limit=", limit, "offset=", offset, "page=", data.current_page)
  
  console.log( "*** API call params:", { currentArticleListId, sort_by: data.sort_by, sort_order: data.sort_order, limit, offset, search_keyword, article_generate_status, cms_post_status })
  const resp = await articlesApi.getAll_filter( currentArticleListId, data.sort_by, data.sort_order, limit, offset, search_keyword, article_generate_status, cms_post_status)
  
  // 👉 CHANGE: handle new response format with pagination metadata
  if (resp.data && resp.data.items) {
    data.items = resp.data.items
    data.total_count = resp.data.total || 0
  } else {
    // Fallback for old format
    data.items = resp.data || []
    data.total_count = data.items.length
  }
  
  console.log( "*** data.items after reload:", data.items ? data.items.length : 0, "items, total:", data.total_count, "search_keyword:", search_keyword)
  console.log( "*** filteredData length:", filteredData.value ? filteredData.value.length : 0, "isSearching:", isSearching.value)

  // 👉 FIX: Add null check before iterating
  if (data.items && Array.isArray(data.items)) {
    for( let i in data.items) {
      const id = data.items[i]?.id
      if (id) {
        data.checked_items[ id] = false
      }
    }
  }
}

// 排序處理函數
const handleSort = async (field) => {
  console.log( "*** handleSort called with field:", field)
  if (data.sort_by === field) {
    // 如果點擊同一個欄位，切換排序方向
    data.sort_order = data.sort_order === "asc" ? "desc" : "asc"
  } else {
    // 如果點擊不同欄位，設定新的排序欄位，預設降序
    data.sort_by = field
    data.sort_order = "desc"
  }
  console.log( "*** After handleSort - sort_by:", data.sort_by, "sort_order:", data.sort_order)
  data.current_page = 1
  await reload_data()
}

// 監聽設定變化，保存到 localStorage 並重新載入數據
watch(() => data.current_page, async (newPage, oldPage) => {
  console.log("📄 Watch current_page changed:", oldPage, "->", newPage)
  // 👉 FIX: 只有在頁面真正改變時才重新載入數據（避免初始化時觸發）
  if (oldPage !== undefined && newPage !== oldPage && newPage > 0) {
    console.log("📄 Reloading data for page:", newPage)
    await reload_data()
  }
  // 保存到 localStorage
  if (article_list_id) {
    saveSettingsToStorage(article_list_id, { page: newPage });
  }
});

watch(() => data.sort_by, (newSortBy) => {
  if (article_list_id) {
    saveSettingsToStorage(article_list_id, { sort_by: newSortBy });
  }
});

watch(() => data.sort_order, (newSortOrder) => {
  if (article_list_id) {
    saveSettingsToStorage(article_list_id, { sort_order: newSortOrder });
  }
});

watch(() => data.filter_article_generate_status, (newFilter) => {
  if (article_list_id) {
    saveSettingsToStorage(article_list_id, { filter_article_generate_status: newFilter });
  }
});

watch(() => data.filter_cms_post_status, (newFilter) => {
  if (article_list_id) {
    saveSettingsToStorage(article_list_id, { filter_cms_post_status: newFilter });
  }
});

// 👉 REMOVED: No need to force page to 1 when searching, as search now uses pagination

// 監聽新建文章的預設值變化
watch(() => data.article.generate_type_id, (newValue) => {
  if (article_list_id && newValue !== null && newValue !== undefined) {
    saveSettingsToStorage(article_list_id, { 
      article_defaults: { generate_type_id: newValue } 
    });
  }
});

watch(() => data.article.ai_model_id, (newValue) => {
  if (article_list_id && newValue !== null && newValue !== undefined) {
    saveSettingsToStorage(article_list_id, { 
      article_defaults: { ai_model_id: newValue } 
    });
  }
});

watch(() => data.article.image_generation_mode, (newValue) => {
  if (article_list_id && newValue !== null && newValue !== undefined) {
    saveSettingsToStorage(article_list_id, { 
      article_defaults: { image_generation_mode: newValue } 
    });
  }
});

watch(() => data.article.image_style_setting, (newValue) => {
  if (article_list_id) {
    saveSettingsToStorage(article_list_id, { 
      article_defaults: { image_style_setting: newValue || "" } 
    });
  }
});

watch(() => data.article.sample_article_id, (newValue) => {
  if (article_list_id) {
    saveSettingsToStorage(article_list_id, { 
      article_defaults: { sample_article_id: newValue } 
    });
  }
});

// --- language detection from prompt template ---
const _langEntries = [
  { label: "繁體中文", keywords: ["繁體中文", "繁体中文", "Traditional Chinese", "台灣中文", "台灣繁體", "中文", "華語", "華文", "中国語", "tiếng Trung"] },
  { label: "簡體中文", keywords: ["簡體中文", "简体中文", "Simplified Chinese"] },
  { label: "日本語",   keywords: ["日本語", "Japanese", "日語", "日文", "tiếng Nhật", "일본어", "ภาษาญี่ปุ่น"] },
  { label: "한국어",   keywords: ["한국어", "韓国語", "Korean", "韓語", "韓文", "朝鮮語", "조선어", "tiếng Hàn"] },
  { label: "English",  keywords: ["English", "英語", "英文", "英国語", "tiếng Anh", "영어", "ภาษาอังกฤษ"] },
  { label: "ภาษาไทย", keywords: ["ภาษาไทย", "Thai", "泰語", "泰文", "タイ語", "태국어", "tiếng Thái"] },
  { label: "Tiếng Việt", keywords: ["Tiếng Việt", "Vietnamese", "越南語", "越南文", "ベトナム語", "베트남어"] },
  { label: "Español",  keywords: ["Español", "Spanish", "西班牙語", "西班牙文", "スペイン語", "tiếng Tây Ban Nha"] },
  { label: "Français", keywords: ["Français", "French", "法語", "法文", "フランス語", "tiếng Pháp"] },
  { label: "Deutsch",  keywords: ["Deutsch", "German", "德語", "德文", "ドイツ語", "tiếng Đức"] },
]
function _escRe(s) { return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') }
const _targetPats = [
  kw => new RegExp(`使用\\s*${_escRe(kw)}`),
  kw => new RegExp(`用\\s*${_escRe(kw)}\\s*[來来]`),
  kw => new RegExp(`以\\s*${_escRe(kw)}\\s*[來来撰書写]`),
  kw => new RegExp(`${_escRe(kw)}\\s*[來来]\\s*撰寫`),
  kw => new RegExp(`絕對要使用\\s*${_escRe(kw)}`),
  kw => new RegExp(`(?:改寫|改写|翻譯|翻译|轉換|转换|轉成|转成)\\s*(?:為|为|成)\\s*${_escRe(kw)}`),
  kw => new RegExp(`(?:寫成|写成)\\s*${_escRe(kw)}`),
  kw => new RegExp(`(?:請|请)\\s*(?:改寫|改写|翻譯|翻译)\\s*(?:為|为|成)?\\s*${_escRe(kw)}`),
  kw => new RegExp(`(?:轉為|转为|換成|换成|改成|譯為|译为|譯成|译成|轉譯為|转译为)\\s*${_escRe(kw)}`),
  kw => new RegExp(`用\\s*${_escRe(kw)}\\s*(?:寫|写|書寫|书写|輸出|输出|產出|产出|生成|編寫|编写|整理|摘要)`),
  kw => new RegExp(`以\\s*${_escRe(kw)}\\s*(?:書寫|书写|輸出|输出|產出|产出|生成|編寫|编写)`),
  kw => new RegExp(`(?:改用|換用|换用)\\s*${_escRe(kw)}`),
  kw => new RegExp(`(?:重寫|重写|重新撰寫|重新撰写|重新編寫|重新编写)\\s*(?:為|为|成)?\\s*${_escRe(kw)}`),
  kw => new RegExp(`(?:撰寫|撰写|編寫|编写)\\s*(?:成|為|为)\\s*${_escRe(kw)}`),
  kw => new RegExp(`${_escRe(kw)}\\s*で(?:書|執筆|記述|リライト|生成|要約|まとめ)`),
  kw => new RegExp(`${_escRe(kw)}\\s*を用いて`),
  kw => new RegExp(`${_escRe(kw)}\\s*で(?:の|作成|出力|回答)`),
  kw => new RegExp(`${_escRe(kw)}\\s*に\\s*(?:書き換|書き直|翻訳|変換|転換|リライト|直す|直し)`),
  kw => new RegExp(`(?:rewrite|write|output|respond|answer)\\s+(?:in|into)\\s+${_escRe(kw)}`, 'i'),
  kw => new RegExp(`MUST\\s+(?:be\\s+in|write\\s+in|use)\\s+${_escRe(kw)}`, 'i'),
  kw => new RegExp(`(?:rewrite|write|translate)\\s*(?:成|為|为|into)?\\s*${_escRe(kw)}`, 'i'),
  kw => new RegExp(`ต้องใช้\\s*${_escRe(kw)}`),
  kw => new RegExp(`เขียนเป็น\\s*${_escRe(kw)}`),
  kw => new RegExp(`ใช้\\s*${_escRe(kw)}\\s*(?:ทั้งหมด|เท่านั้น|ในการ|เขียน|แปล)`),
  kw => new RegExp(`แปล\\s*(?:เป็น|เข้า)\\s*${_escRe(kw)}`),
  kw => new RegExp(`(?:เขียนใหม่|เรียบเรียง|สรุป|แก้ไข|เปลี่ยน|แปลง|ร่าง|พิมพ์)\\s*(?:เป็น|ใน|ด้วย)\\s*${_escRe(kw)}`),
  kw => new RegExp(`เขียน\\s*(?:ด้วย|เป็น)\\s*${_escRe(kw)}`),
  kw => new RegExp(`ใช้\\s*${_escRe(kw)}\\s*(?:เขียน|แปล|สรุป|เรียบเรียง|ร่าง)`),
  kw => new RegExp(`${_escRe(kw)}\\s*(?:로|으로)\\s*(?:작성|번역|쓰기|쓰다|출력|변환|바꾸|고치|리라이트|생성|요약|정리|편집|작문)`),
  kw => new RegExp(`${_escRe(kw)}\\s*(?:를|을)\\s*(?:사용|쓰다|쓰기)`),
  kw => new RegExp(`${_escRe(kw)}\\s*(?:로|으로)\\s*다시\\s*(?:쓰다|쓰기|작성)`),
  kw => new RegExp(`用\\s*${_escRe(kw)}\\s*(?:rewrite|write|translate|generate|改寫|改写|撰寫|撰写|翻譯|翻译)`),
  kw => new RegExp(`${_escRe(kw)}\\s*(?:で|に)\\s*(?:rewrite|write|translate|generate)`),
]
function _detectScriptLanguage(text) {
  let thai = 0, hiraKata = 0, hangul = 0, cjk = 0, latin = 0, viet = 0
  for (const ch of text) {
    const c = ch.codePointAt(0)
    if (c >= 0x0E00 && c <= 0x0E7F) thai++
    else if ((c >= 0x3040 && c <= 0x309F) || (c >= 0x30A0 && c <= 0x30FF)) hiraKata++
    else if ((c >= 0xAC00 && c <= 0xD7AF) || (c >= 0x1100 && c <= 0x11FF) || (c >= 0x3130 && c <= 0x318F)) hangul++
    else if (c >= 0x4E00 && c <= 0x9FFF) cjk++
    else if ((c >= 0x00C0 && c <= 0x024F) || (c >= 0x1E00 && c <= 0x1EFF)) viet++
    else if (c >= 0x0041 && c <= 0x007A) latin++
  }
  const total = thai + hiraKata + hangul + cjk + latin + viet
  if (total === 0) return null
  if (thai > 0 && thai >= hiraKata && thai >= hangul) return "ภาษาไทย"
  if (hiraKata > 0) return "日本語"
  if (hangul > 0 && hangul >= cjk) return "한국어"
  if (cjk > 0 && cjk >= latin) return "繁體中文"
  if (viet > latin * 0.05 && latin > 0) return "Tiếng Việt"
  if (latin > 0) return "English"
  return null
}
const detectedPromptLanguage = computed(() => {
  const ptId = data.article.prompt_template_id
  if (!ptId) return null
  const content = data.prompt_templates_content[ptId]
  if (!content) return null

  // Phase 1: explicit target-language patterns
  for (const entry of _langEntries) {
    for (const kw of entry.keywords) {
      if (_targetPats.some(fn => fn(kw).test(content))) return entry.label
    }
  }

  // Phase 2: detect the dominant script of the prompt text itself
  return _detectScriptLanguage(content)
})

watch(() => data.article.prompt_template_id, (newValue) => {
  if (article_list_id) {
    saveSettingsToStorage(article_list_id, { 
      article_defaults: { prompt_template_id: newValue } 
    });
  }
});

watch(() => data.article.futured_media_id, (newValue) => {
  if (article_list_id) {
    saveSettingsToStorage(article_list_id, { 
      article_defaults: { futured_media_id: newValue } 
    });
  }
});

watch(() => data.article.connection_id, (newValue) => {
  if (article_list_id) {
    saveSettingsToStorage(article_list_id, { 
      article_defaults: { connection_id: newValue } 
    });
  }
});

// 監聽 article_list_id 變化，載入對應的設定
watch(() => route.params.article_list_id, async (newListId) => {
  const parsedId = parseInt(newListId) || null;
  if (parsedId) {
    const settings = loadSettingsFromStorage(parsedId);
    // 載入所有設定
    data.current_page = settings.page;
    data.sort_by = settings.sort_by;
    data.sort_order = settings.sort_order;
    data.filter_article_generate_status = settings.filter_article_generate_status;
    data.filter_cms_post_status = settings.filter_cms_post_status;
    
    // 載入搜索關鍵字
    searchKey.value = settings.search_key || "";
    filterKey.searchKey = settings.search_key || null;
    
    // 載入新建文章的預設值
    data.article.generate_type_id = settings.article_defaults.generate_type_id;
    data.article.ai_model_id = settings.article_defaults.ai_model_id;
    data.article.image_generation_mode = settings.article_defaults.image_generation_mode;
    data.article.image_style_setting = settings.article_defaults.image_style_setting;
    data.article.sample_article_id = settings.article_defaults.sample_article_id;
    data.article.prompt_template_id = settings.article_defaults.prompt_template_id;
    data.article.futured_media_id = settings.article_defaults.futured_media_id;
    data.article.connection_id = settings.article_defaults.connection_id;
    
    // 清空舊的數據，避免顯示其他 article_list 的數據
    // 直接重新賦值整個對象，確保 Vue 響應式系統正確追蹤
    data.sample_articles = {};
    data.prompt_templates = {};
    data.categories = {};
    data.futured_medias = {};
    data.connections = {};
    data.connections_data = {};
    
    // 重新載入數據
    await reload_data();
    
    // 重新載入樣本文章、提示模板等數據
    const resp_2 = await sample_articles_Api.getAll_filter(parsedId);
    const newSampleArticles = {};
    for( const i in resp_2.data) {
      const item = resp_2.data[i]
      newSampleArticles[ item.id] = item.sample_article_name
    }
    data.sample_articles = newSampleArticles;
    
    // 檢查保存的 sample_article_id 是否在新的 article_list 中存在
    if (data.article.sample_article_id && !data.sample_articles[data.article.sample_article_id]) {
      data.article.sample_article_id = null;
      saveSettingsToStorage(parsedId, { 
        article_defaults: { sample_article_id: null } 
      });
    }

    const resp_3 = await prompt_templates_Api.getAll_filter(parsedId);
    const newPromptTemplates = {};
    const newPromptTemplatesContent = {};
    for( const i in resp_3.data) {
      const item = resp_3.data[i]
      newPromptTemplates[ item.id] = item.prompt_template_name
      newPromptTemplatesContent[ item.id] = item.prompt_template_content
    }
    data.prompt_templates = newPromptTemplates;
    data.prompt_templates_content = newPromptTemplatesContent;
    
    // 檢查保存的 prompt_template_id 是否在新的 article_list 中存在
    if (data.article.prompt_template_id && !data.prompt_templates[data.article.prompt_template_id]) {
      data.article.prompt_template_id = null;
      saveSettingsToStorage(parsedId, { 
        article_defaults: { prompt_template_id: null } 
      });
    }

    const resp_4 = await categories_Api.getAll_filter(parsedId);
    const newCategories = {};
    for( const i in resp_4.data) {
      const item = resp_4.data[i]
      newCategories[ item.id] = item.category_name
    }
    data.categories = newCategories;

    const resp_5 = await futured_medias_Api.getAll_filter(parsedId);
    const newFuturedMedias = {};
    for( const i in resp_5.data) {
      const item = resp_5.data[i]
      newFuturedMedias[ item.id] = item.file_name
    }
    data.futured_medias = newFuturedMedias;
    
    // 檢查保存的 futured_media_id 是否在新的 article_list 中存在
    if (data.article.futured_media_id && !data.futured_medias[data.article.futured_media_id]) {
      data.article.futured_media_id = null;
      saveSettingsToStorage(parsedId, { 
        article_defaults: { futured_media_id: null } 
      });
    }

    const resp_6 = await connectionsApi.getAll_filter(parsedId);
    const newConnections = {};
    const newConnectionsData = {};
    for( const i in resp_6.data) {
      const item = resp_6.data[i]
      newConnections[ item.id] = item.connection_name
      newConnectionsData[ item.id] = item
    }
    data.connections = newConnections;
    data.connections_data = newConnectionsData;
    
    // 檢查保存的 connection_id 是否在新的 article_list 中存在
    if (data.article.connection_id && !data.connections[data.article.connection_id]) {
      data.article.connection_id = null;
      saveSettingsToStorage(parsedId, { 
        article_defaults: { connection_id: null } 
      });
    }
  }
});

onMounted(async () => {
  // 確保 article_list_id 存在（在 onMounted 時重新獲取，確保路由參數已準備好）
  const currentArticleListId = parseInt(route.params.article_list_id) || null;
  if (!currentArticleListId) {
    console.error("article_list_id is missing from route params:", route.params);
    return;
  }
  
  if( ! await accessControlApi.check_option_access()) {
    await router.push(`/forwriting/options_error`)
    return
  }

  // 載入當前 article_list 的所有設定
  const settings = loadSettingsFromStorage(currentArticleListId);
  data.current_page = settings.page;
  data.sort_by = settings.sort_by;
  data.sort_order = settings.sort_order;
  data.filter_article_generate_status = settings.filter_article_generate_status;
  data.filter_cms_post_status = settings.filter_cms_post_status;
  
  // 載入搜索關鍵字
  searchKey.value = settings.search_key || "";
  filterKey.searchKey = settings.search_key || null;
  
  // 載入新建文章的預設值
  data.article.generate_type_id = settings.article_defaults.generate_type_id;
  data.article.ai_model_id = settings.article_defaults.ai_model_id;
  data.article.image_generation_mode = settings.article_defaults.image_generation_mode;
  data.article.image_style_setting = settings.article_defaults.image_style_setting;
  data.article.sample_article_id = settings.article_defaults.sample_article_id;
  data.article.prompt_template_id = settings.article_defaults.prompt_template_id;
  data.article.futured_media_id = settings.article_defaults.futured_media_id;
  data.article.connection_id = settings.article_defaults.connection_id;

  // 清空所有舊數據，確保不會顯示其他 article_list 的數據
  // 直接重新賦值整個對象，確保 Vue 響應式系統正確追蹤
  data.sample_articles = {};
  data.prompt_templates = {};
  data.categories = {};
  data.futured_medias = {};
  data.connections = {};
  data.connections_data = {};

  await reload_data()

  // load generate_types from db
  const resp_1 = await articlesApi.get_generate_types()
  for( const i in resp_1.data) {
    const item = resp_1.data[i]
    data.generate_types[ item.id] = item.generate_type_name
  }

  // load sample_articles from db
  const resp_2 = await sample_articles_Api.getAll_filter( currentArticleListId)
  // const resp_2 = await articlesApi.get_sample_articles_filter( article_list_id)
  const newSampleArticles = {};
  for( const i in resp_2.data) {
    const item = resp_2.data[i]
    newSampleArticles[ item.id] = item.sample_article_name
  }
  data.sample_articles = newSampleArticles;
  
  // 檢查保存的 sample_article_id 是否存在
  if (data.article.sample_article_id && !data.sample_articles[data.article.sample_article_id]) {
    data.article.sample_article_id = null;
    saveSettingsToStorage(currentArticleListId, { 
      article_defaults: { sample_article_id: null } 
    });
  }

  // load prompt_templates from db
  const resp_3 = await prompt_templates_Api.getAll_filter( currentArticleListId)
  // const resp_3 = await articlesApi.get_prompt_templates_filter( article_list_id)
  const newPromptTemplates = {};
  const newPromptTemplatesContent2 = {};
  for( const i in resp_3.data) {
    const item = resp_3.data[i]
    newPromptTemplates[ item.id] = item.prompt_template_name
    newPromptTemplatesContent2[ item.id] = item.prompt_template_content
  }
  data.prompt_templates = newPromptTemplates;
  data.prompt_templates_content = newPromptTemplatesContent2;
  
  // 檢查保存的 prompt_template_id 是否存在
  if (data.article.prompt_template_id && !data.prompt_templates[data.article.prompt_template_id]) {
    data.article.prompt_template_id = null;
    saveSettingsToStorage(currentArticleListId, { 
      article_defaults: { prompt_template_id: null } 
    });
  }
  // console.log( "prompt_templates:", data.prompt_templates)

  // load categories from db
  const resp_4 = await categories_Api.getAll_filter( currentArticleListId)
  // const resp_4 = await articlesApi.get_categories_filter( article_list_id)
  const newCategories = {};
  for( const i in resp_4.data) {
    const item = resp_4.data[i]
    newCategories[ item.id] = item.category_name
  }
  data.categories = newCategories;
  // console.log( "categories:", data.categories)

  // load futured_medias from db
  const resp_5 = await futured_medias_Api.getAll_filter( currentArticleListId)
  const newFuturedMedias = {};
  for( const i in resp_5.data) {
    const item = resp_5.data[i]
    newFuturedMedias[ item.id] = item.file_name
  }
  data.futured_medias = newFuturedMedias;
  
  // 檢查保存的 futured_media_id 是否存在
  if (data.article.futured_media_id && !data.futured_medias[data.article.futured_media_id]) {
    data.article.futured_media_id = null;
    saveSettingsToStorage(currentArticleListId, { 
      article_defaults: { futured_media_id: null } 
    });
  }

  // load connections from db
  const resp_6 = await connectionsApi.getAll_filter( currentArticleListId)
  const newConnections = {};
  const newConnectionsData = {};
  for( const i in resp_6.data) {
    const item = resp_6.data[i]
    newConnections[ item.id] = item.connection_name
    // 存儲連接的完整信息（包括 integration）
    newConnectionsData[ item.id] = item
  }
  data.connections = newConnections;
  data.connections_data = newConnectionsData;
  
  // 檢查保存的 connection_id 是否存在
  if (data.article.connection_id && !data.connections[data.article.connection_id]) {
    data.article.connection_id = null;
    saveSettingsToStorage(currentArticleListId, { 
      article_defaults: { connection_id: null } 
    });
  }

  // load ai_models from db
  const resp_7 = await articlesApi.get_ai_models()
  for( const i in resp_7.data) {
    const item = resp_7.data[i]
    data.ai_models[ item.id] = item.model_name
  }
  
  // 如果沒有 ai_model_id，設置預設值
  if (!data.article.ai_model_id) {
    data.article.ai_model_id = 1  // 預設為 Claude Sonnet 4.5
  }

  // for signin to open modal new
  if( is_new)
    show_modal_article_new.value = true

  // check the point_limit
  // 👉 FIX: Check if ref_CheckPointLimit.value exists before calling check()
  if (ref_CheckPointLimit.value && typeof ref_CheckPointLimit.value.check === 'function') {
    point_limit.is_over = await ref_CheckPointLimit.value.check()
  } else {
    console.warn("CheckPointLimit component is not available yet")
    point_limit.is_over = false
  }

  // for testing
  // show_modal_article_new.value = true
});

const onSelectAllChange = async( e) =>
{
  const checked_val = e.target.checked
  for( const i in data.checked_items)
    data.checked_items[i] = checked_val
  console.log( "checked_items:", data.checked_items)
}

const post_selected = async () => {
  console.log( "post_selected()...")
  isLoading.value = true

  for( const article_id in data.checked_items) {
    console.log( "checked:", article_id, data.checked_items[ article_id])
    if( data.checked_items[ article_id]) {
      console.log( "post article_id:", article_id)

      // 從文章數據中獲取 connection_id
      const article = data.items.find(item => item.id === parseInt(article_id))
      const connection_id = article?.connection_id || 0

      // connection_id 可以為 0（選擇「無」時只保存不發布）
      if (connection_id === null || connection_id === undefined) {
        console.warn(`Skipping article ${article_id}: connection_id is null or undefined`)
        alert(`文章 ${article_id} 未設置連接，請先選擇連接或選擇「無」。`)
        continue
      }

      if (connection_id === 0) {
        console.log(`Article ${article_id} has no connection (connection_id = 0), will save only without posting`)
      } else {
        console.log(`Posting article ${article_id} with connection_id: ${connection_id}`)
      }

      // 調用 backend API，傳遞 connection_id（0 表示只保存不發布）
      const resp = await articlesApi.post( article_id, connection_id)
      console.log("*** resp from articlesApi.post:", resp)

      if( resp.status !== 200) {
        throw new Error("articlesApi.post() ERROR")
      }
    }
  }

  isLoading.value = false
}

// button refresh
const refreshPage = async () => {
  await reload_data()
  // window.location.reload();
}

const categories_selected = ref({});
const category_index = ref( "");
watch( category_index, (new_index) => {
  let ids = []
  if( new_index === "ALL")
  {
    ids = Object.keys( data.categories)
  }
  else if( !( new_index in categories_selected.value) && new_index !== "")
  {
    ids.push( new_index )
  }
  console.log( "ids:", ids)

  for( let i in ids)
  {
    console.log( i, ids[i] );
    let idx = ids[i]
    categories_selected.value[ idx] = data.categories[ idx]
    console.log( "categories_selected:", categories_selected.value)
    category_index.value = ""
  }
})
const del = (index) => {
  if (category_index.value.length === 0) {
    delete categories_selected.value[index]
  }
}

// ==== modal_over_points ====
const ref_CheckPointLimit = ref(null)
const point_limit = reactive({
  is_over: false,
})

const isLoading = ref(false);

// 檢查選中的連接是否為 WordPress
const isWordPressConnection = computed(() => {
  const connectionId = data.article.connection_id
  if (!connectionId || connectionId === 0) {
    return false
  }
  const connection = data.connections_data[connectionId]
  return connection && connection.integration === 'wordpress'
})

</script>

<template>
  <LoadingIcon :is-loading="isLoading"></LoadingIcon>

  <!-- modal_over_points -->
  <CheckPointLimit ref="ref_CheckPointLimit"></CheckPointLimit>

  <!-- Delete_modal -->
  <div v-if="show_modal_delete" class="modal-del">
    <div class="modal-content">

      <div class="row" style="margin-top: 20px;text-align: center">
        <p>{{ delete_modal_msg }}</p>
      </div>

      <!-- 2 buttons -->
      <div class="row" style="margin-top: 20px;margin-left: 10px; margin-right: 10px">
        <button class="btn btn-primary"
                @click="handleDelete_confirm">
          {{ $t( "Articles.OK") }}</button>
      </div>

      <div class="row" style="margin-top: 20px;margin-left: 10px; margin-right: 10px">
        <button class="btn btn-primary"
                @click="show_modal_delete = false">
          {{ $t( "Articles.cancel") }}</button>
      </div>

    </div>
  </div>
  <!-- end of Delete_modal -->

  <!-- article_new_modal" -->
  <div v-if="show_modal_article_new" class="modal-create">
    <div class="modal-content">

      <div class="row" >
        <h4>{{ $t( "Articles.create") }}</h4>
      </div>

      <div class="row" style="margin-top: 20px">

        <table>
          <tbody>

          <!-- [ 0, 1, 2, 3]: generate_type -->
          <tr class="tr-line">
            <td class="td-left">
              <label class="required">{{ $t( "Articles.generate_type") }}:</label>
            </td>
            <td class="td-right">

              <select class="form-select td-combo"
                      v-model="data.article.generate_type_id"
                      @change="generate_type_changed">
<!--                <option value=0> - </option>-->
                <option value=3>{{ $t( "Articles.generate_type_3") }}</option>
                <option value=4>{{ $t( "Articles.generate_type_4") }}</option>
                <option value=5>{{ $t( "Articles.generate_type_5") }}</option>
                <option value=6>{{ $t( "Articles.generate_type_6") }}</option>
              </select>

            </td>
          </tr>

          <!-- [ 3, 4]: urls -->
          <tr class="tr-line"
              v-show="[ 3, 4].includes( parseInt(data.article.generate_type_id))">
            <td class="td-left">
              <label class="required">{{ $t( "Articles.urls") }}:</label>
            </td>
            <td class="td-right">
              <textarea rows="3" type="text" class="td-text"
                        :placeholder="$t('Articles_Edit.urls_placeholder')"
                        v-model="data.article.urls" ></textarea>
              <!-- URL/テキスト モードバッジ & 一次ソース判定ボタン -->
              <div style="display: flex; align-items: center; gap: 8px; margin-left: 2px; margin-top: 4px; flex-wrap: wrap;">
                <span v-if="data.article.urls"
                      class="mode-badge"
                      :class="isUrlMode ? 'mode-url' : 'mode-text'">
                  {{ isUrlMode ? 'URL' : 'テキスト' }}
                </span>
                <button v-if="isUrlMode"
                        class="btn btn-outline-secondary btn-sm"
                        style="font-size: 12px;"
                        @click="check_primary_source"
                        :disabled="primarySourceCheck.loading || !data.article.urls">
                  <span v-if="primarySourceCheck.loading">{{ $t('Articles_Edit.primary_source_checking') }}</span>
                  <span v-else>{{ $t('Articles_Edit.primary_source_check') }}</span>
                </button>
              </div>
              <div class="urls-hint">{{ $t('Articles_Edit.urls_hint') }}</div>
              <!-- 一次ソース判定結果 -->
              <div v-if="primarySourceCheck.checked" style="margin-left: 10px; margin-top: 8px;">
                <div v-for="(result, index) in primarySourceCheck.results" :key="index"
                     class="primary-source-result"
                     :class="{
                       'result-primary': result.is_primary_source === true,
                       'result-secondary': result.is_primary_source === false,
                       'result-unknown': result.is_primary_source === null
                     }">
                  <div style="display: flex; align-items: center; gap: 8px;">
                    <span class="badge"
                          :class="{
                            'bg-success': result.is_primary_source === true,
                            'bg-warning text-dark': result.is_primary_source === false,
                            'bg-secondary': result.is_primary_source === null
                          }">
                      {{ result.is_primary_source === true
                         ? $t('Articles_Edit.primary_source_yes')
                         : result.is_primary_source === false
                           ? $t('Articles_Edit.primary_source_no')
                           : $t('Articles_Edit.primary_source_unknown') }}
                    </span>
                    <span v-if="result.confidence > 0" style="font-size: 12px; color: #666;">
                      ({{ $t('Articles_Edit.primary_source_confidence') }}: {{ result.confidence }}%)
                    </span>
                    <span style="font-size: 12px; color: #888;">
                      {{ result.source_type }}
                    </span>
                  </div>
                  <div style="font-size: 11px; color: #999; margin-top: 2px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 600px;">
                    {{ result.url }}
                  </div>
                  <div style="font-size: 12px; color: #555; margin-top: 2px;">
                    {{ result.reason }}
                  </div>
                </div>
              </div>
            </td>
          </tr>

          <!-- [ 5]: audio file upload -->
          <tr class="tr-line"
              v-show="parseInt(data.article.generate_type_id) === 5">
            <td class="td-left">
              <label class="required">{{ $t("Articles.audio_file") }}:</label>
            </td>
            <td class="td-right">
              <input type="file"
                     accept="audio/*,video/mp4,video/mpeg"
                     style="display: none"
                     ref="audio_input_ref"
                     @change="on_audio_file_change" />
              <button class="btn btn-outline-secondary btn-sm"
                      @click="$refs.audio_input_ref.click()">
                {{ $t("Articles.audio_select") }}
              </button>
              <span v-if="audio_file_name" style="margin-left: 10px; font-size: 13px; color: #333;">
                {{ audio_file_name }}
              </span>
              <div style="font-size: 12px; color: #888; margin-top: 4px;">
                {{ $t("Articles.audio_hint") }}
              </div>
            </td>
          </tr>

          <!-- [ 5]: audio supplement -->
          <tr class="tr-line"
              v-show="parseInt(data.article.generate_type_id) === 5">
            <td class="td-left">
              <label>{{ $t("Articles.audio_supplement") }}:</label>
            </td>
            <td class="td-right">
              <textarea rows="3" class="td-text"
                        :placeholder="$t('Articles.audio_supplement_placeholder')"
                        v-model="audio_supplement"></textarea>
            </td>
          </tr>

          <!-- [ 3, 4, 5, 6]: sample_article -->
          <tr class="tr-line"
              v-show="[ 3, 4, 5, 6].includes( parseInt(data.article.generate_type_id))">
            <td class="td-left">
              <label :class="parseInt(data.article.generate_type_id) !== 6 ? 'required' : ''">{{ $t( "Articles.sample_article_name") }}:</label>
            </td>
            <td class="td-right">
              <select class="form-select td-combo"
              v-model="data.article.sample_article_id">
                <option value=""> - </option>
                <option :value="index" v-for="(item, index) in data.sample_articles" :key="index">
                  {{item}}</option>
              </select>
            </td>
          </tr>

          <!-- [ 3, 4, 5, 6]: prompt_template -->
          <tr class="tr-line"
              v-show="[ 3, 4, 5, 6].includes( parseInt(data.article.generate_type_id))">
            <td class="td-left">
              <label :class="parseInt(data.article.generate_type_id) !== 6 ? 'required' : ''">{{ $t( "Articles.prompt_template_name") }}:</label>
            </td>
            <td class="td-right">
              <div class="d-flex align-items-center gap-2">
                <select class="form-select td-combo"
                v-model="data.article.prompt_template_id">
                  <option value=""> - </option>
                  <option :value="index"
                          v-for="(item, index) in data.prompt_templates" :key="index">
                    {{item}}</option>
                </select>
                <span v-if="detectedPromptLanguage"
                      class="badge bg-info text-dark text-nowrap"
                      style="font-size: 0.78rem;">
                  {{ detectedPromptLanguage }}
                </span>
              </div>
            </td>
          </tr>

          <!-- 追加プロンプト / AI命令 -->
          <tr class="tr-line"
              v-show="[ 3, 4, 5, 6].includes( parseInt(data.article.generate_type_id))">
            <td class="td-left">
              <label :class="parseInt(data.article.generate_type_id) === 6 ? 'required' : ''">
                {{ parseInt(data.article.generate_type_id) === 6
                  ? $t("Articles.agent_instruction")
                  : $t("Articles.additional_prompt") }}:
              </label>
            </td>
            <td class="td-right">
              <textarea rows="4" class="td-text"
                        :placeholder="parseInt(data.article.generate_type_id) === 6
                          ? $t('Articles.agent_instruction_placeholder')
                          : $t('Articles.additional_prompt_placeholder')"
                        v-model="data.article.additional_prompt"></textarea>
            </td>
          </tr>

          <!-- [ 0, 1, 2, 3]: authentication_type/connection_id -->
          <tr class="tr-line">
            <td class="td-left">
              <label>{{ $t( "Articles.connect_to") }}:</label>
            </td>
            <td class="td-right">
              <select class="form-select td-combo"
                      v-model="data.article.connection_id">
                <option value=0>{{ $t("Articles.none") }}</option>
                <option :value="index" v-for="(item, index) in data.connections" :key="index">
                  {{item}}</option>
              </select>
            </td>
          </tr>

          <!-- [ 3, 4]: FW_categories/category_name -->
          <tr class="tr-line"
              v-show="[ 3, 4, 5, 6].includes( parseInt(data.article.generate_type_id))">
            <td class="td-left">
              <label>{{ $t( "Articles.category_name") }}:</label>
            </td>
            <td class="td-right">

                <div class="tags rounded-pill d-flex flex-wrap mb-3 shadow-sm"
                     v-for="(item, index) in categories_selected" :key="index"
                >
                  <span class="content">{{ item }}</span>
                  <span class="btn-animation del" @click="del(index)">&times;</span>
                </div>

            </td>
          </tr>

          <!-- [ 3, 4]: FW_categories/category_name select -->
          <tr class="tr-line"
              v-show="[ 3, 4, 5, 6].includes( parseInt(data.article.generate_type_id))">
            <td class="td-left">
            </td>
            <td class="td-right">

              <select class="form-select td-combo" style="margin-top: 1px"
                      v-model="category_index">
                <option value=""> - </option>
                <option value="ALL">ALL</option>
                <option v-for="(item, index) in data.categories"
                        :value="index" :key="index">
                  {{item}}</option>
              </select>

            </td>
          </tr>

          <!-- [ 3, 4]: FW_futured_medias/file_name -->
          <tr class="tr-line"
              v-show="[ 3, 4, 5, 6].includes( parseInt(data.article.generate_type_id))">
            <td class="td-left">
              <label>{{ $t( "Articles.futured_media") }}:</label>
            </td>
            <td class="td-right">
              <select class="form-select td-combo"
                      v-model="data.article.futured_media_id">
                <option  value=0> - </option>
                <!-- 如果連接是 WordPress，添加 AI Generation 選項（使用 -1 作為特殊值） -->
                <option v-if="isWordPressConnection" value="-1">{{ $t("Articles.ai_generation") }}</option>
                <option :value="index" v-for="(item, index) in data.futured_medias" :key="index">
                  {{item}}</option>
              </select>
            </td>
          </tr>

          <!-- AIモデル -->
          <tr class="tr-line">
            <td class="td-left">
              <label class="required">{{ $t( "Articles.ai_model") }}:</label>
            </td>
            <td class="td-right">
              <select class="form-select td-combo"
                      v-model="data.article.ai_model_id">
                <option :value="id" v-for="(name, id) in data.ai_models" :key="id">
                  {{ name }}
                </option>
              </select>
            </td>
          </tr>

          <!-- 記事内画像アップロード -->
          <tr class="tr-line" v-show="parseInt(data.article.generate_type_id) !== 4">
            <td class="td-left">
              <label>{{ $t("Articles.inline_images") }}:</label>
            </td>
            <td class="td-right">
              <input type="file" multiple accept="image/*"
                     style="display: none" ref="inline_image_input_ref"
                     @change="on_inline_image_change" />
              <button class="btn btn-outline-secondary btn-sm"
                      :disabled="data.article.image_generation_mode"
                      @click="$refs.inline_image_input_ref.click()">
                {{ $t("Articles.inline_images_select") }}
              </button>
              <div v-if="inline_image_names.length > 0" style="margin-top: 6px;">
                <div v-for="(name, idx) in inline_image_names" :key="idx"
                     style="display: inline-flex; align-items: center; gap: 4px; margin: 2px 8px 2px 0; padding: 2px 8px; background: #e9ecef; border-radius: 4px; font-size: 12px;">
                  {{ name }}
                  <span style="cursor: pointer; color: #dc3545;" @click="remove_inline_image(idx)">&#x2715;</span>
                </div>
              </div>
              <div style="font-size: 12px; color: #888; margin-top: 4px;">
                {{ $t("Articles.inline_images_hint") }}
              </div>
            </td>
          </tr>

          <!-- 画像生成モード -->
          <tr class="tr-line">
            <td class="td-left">
              <label>{{ $t( "Articles.image_generation_mode") }}:</label>
            </td>
            <td class="td-right">
              <div style="display: flex; align-items: center; gap: 10px; padding-top: 2px;">
              <label class="switch">
                <input type="checkbox"
                       v-model="data.article.image_generation_mode"
                       :disabled="parseInt(data.article.generate_type_id) === 4 || inline_image_files.length > 0">
                <span class="slider round"></span>
              </label>
              <!-- 画像スタイル設定 -->
              <select v-if="data.article.image_generation_mode && parseInt(data.article.generate_type_id) !== 4" 
                      class="form-select td-combo"
                      v-model="data.article.image_style_setting"
                      style="width: 200px;">
                <option value="">{{ $t( "Articles.select_image_style") }}</option>
                <option value="realistic">{{ $t( "Articles.image_style_realistic") }}</option>
                <option value="anime">{{ $t( "Articles.image_style_anime") }}</option>
                <option value="illustration">{{ $t( "Articles.image_style_illustration") }}</option>
                <option value="photography">{{ $t( "Articles.image_style_photography") }}</option>
              </select>
              <!-- 画像転載モード提示 -->
              <span v-if="parseInt(data.article.generate_type_id) === 4"
                    style="color: #666; font-size: 12px;">
                (画像転載モードでは AI 画像生成は無効です)
              </span>
              </div>
            </td>
          </tr>

          </tbody>
        </table>

      </div>

      <!-- 2 buttons-->
      <div class="row" style="margin-top: 20px">

        <button class="btn btn-primary"
                style="width: 120px; margin-left: 20px"
                v-show="data.article.generate_type_id > 0"
                @click="open_articles_new">
          {{ $t( "Articles.regenerate") }}</button>

        <button class="btn btn-primary" style="width: 120px; margin-left: 20px"
                @click="show_modal_article_new=false">
          {{ $t( "Articles.cancel") }}</button>
      </div>

    </div>
  </div>
  <!-- end of article_new_modal" -->

  <!-- article_check_modal" -->
  <div v-if="modal_check.show" class="modal-del">
    <div class="modal-content">

      <div class="row" style="margin-top: 20px;text-align: center">
        <p>{{ modal_check.msg }}</p>
      </div>

      <!-- 1 buttons -->
      <div class="row" style="margin-top: 20px;margin-left: 10px; margin-right: 10px">
        <button class="btn btn-primary"
                @click="modal_check.show=false">
          {{ $t( "Articles.OK") }}</button>
      </div>
    </div>
  </div>
  <!-- end of article_check_modal" -->

  <div class="container">

    <!-- title-->
    <div class="row">
      <h4>{{ $t( "Articles.articles") }}</h4>
    </div>

    <!-- key for filer(關鍵詞過濾) -->
    <div class="row">
      <div class="column">

        <label for="search-keyword">{{ $t( "Articles.search_keyword") }}:</label>
        <input type="text" id="search-keyword" v-model="searchKey"
               style="padding: 0 10px; margin: 10px;"/>

        <button class="btn btn-primary" @click="handleFilter"
                style="padding: 0 50px; margin: 10px;">
          {{ $t( "Articles.filter") }}</button>
      </div>

    </div>

    <!-- 2 combobox(2個下拉選項) -->
    <div class="row">

      <table style="width: 720px">
        <tbody>

        <tr>

          <td style="width: 120px;text-align: right">
            <label for="article_generate_status">
              {{ $t( "Articles.article_generate_status") }}:</label>
          </td>
          <td style="width: 200px">
            <select id="article_generate_status" class="form-select"
                    v-model="data.filter_article_generate_status"
                    @change="onChange_article_generate_status">
              <option value=-1> - </option>
              <option value=0>{{$t( "Articles.article_generate_status_0") }}</option>
              <option value=1>{{$t( "Articles.article_generate_status_1") }}</option>
              <option value=2>{{$t( "Articles.article_generate_status_2") }}</option>
              <option value=3>{{$t( "Articles.article_generate_status_3") }}</option>
            </select>
          </td>

          <td style="width: 40px;"></td>

          <td style="width: 120px;text-align: right">
            <label for="cms_post_status">
              {{ $t( "Articles.cms_post_status") }}:</label>
          </td>
          <td style="width: 200px">
            <select id="cms_post_status" class="form-select"
                    v-model="data.filter_cms_post_status"
                    @change="onChange_cms_post_status">
              <option value=-1> - </option>
              <option value=0>{{$t( "Articles.cms_post_status_0") }}</option>
              <option value=1>{{$t( "Articles.cms_post_status_1") }}</option>
              <option value=2>{{$t( "Articles.cms_post_status_2") }}</option>
              <option value=3>{{$t( "Articles.cms_post_status_3") }}</option>
            </select>
          </td>
        </tr>

        </tbody>
      </table>

    </div>

    <!-- 3 button -->
    <div class="row">
      <div class="column" style="text-align: right">

        <button class="btn btn-primary"
                @click="show_modal_article_new = true"
                :disabled="point_limit.is_over"
                style="padding: 0 50px; margin: 10px;">
          {{ $t( "Articles.create") }}</button>

        <button class="btn btn-primary" style="padding: 0 50px; margin: 10px;"
                @click="post_selected">
          {{ $t( "Articles.post") }}</button>

        <button class="btn btn-primary" @click="refreshPage"
                style="padding: 0 50px; margin: 10px;">
          {{ $t( "Articles.refresh") }}
        </button>

      </div>
    </div>

    <!-- select/de-select -->
    <div class="row">
      <div class="column">
        <input id="select_all" type="checkbox" @change="onSelectAllChange"/>
        <label style="padding: 0 10px; margin: 1px;" for="select_all">
          {{ $t( "Articles.select_all") }}</label>
      </div>
    </div>

    <div class="row">

      <!-- the main table(主體表格) -->
      <table class="table table-hover articles-table">
        <colgroup>
          <col style="width: 40px;">    <!-- 対象 -->
          <col style="width: 45px;">    <!-- No. -->
          <col style="width: 28%;">     <!-- タイトル -->
          <col style="width: 120px;">   <!-- 生成タイプ -->
          <col style="width: 13%;">     <!-- 投稿先リンク -->
          <col style="width: 85px;">    <!-- 生成状況 -->
          <col style="width: 85px;">    <!-- 投稿状況 -->
          <col style="width: 122px;">   <!-- 生成日時 -->
          <col style="width: 122px;">   <!-- 更新日時 -->
          <col style="width: 110px;">   <!-- 操作 -->
        </colgroup>
        <thead>
        <tr>
          <th scope="col">{{ $t( "Articles.selected") }}</th>
          <th scope="col" style="text-align: center;">No.</th>
          <th scope="col" style="cursor: pointer;" @click="handleSort('article_title')">
            {{ $t( "Articles.article_title") }}
            <span v-if="data.sort_by === 'article_title'">
              {{ data.sort_order === 'asc' ? '↑' : '↓' }}
            </span>
          </th>
          <th scope="col">{{ $t( "Articles.generate_type") }}</th>
          <th scope="col">{{ $t( "Articles.posted_url") }}</th>
          <th scope="col" style="text-align: center;">{{ $t( "Articles.article_generate_status") }}</th>
          <th scope="col" style="text-align: center;">{{ $t( "Articles.cms_post_status") }}</th>
          <th scope="col" style="cursor: pointer;" @click="handleSort('created_at')">
            {{ $t( "Articles.created_at") }}
            <span v-if="data.sort_by === 'created_at'">
              {{ data.sort_order === 'asc' ? '↑' : '↓' }}
            </span>
          </th>
          <th scope="col" style="cursor: pointer;" @click="handleSort('modified_at')">
            {{ $t( "Articles.modified_at") }}
            <span v-if="data.sort_by === 'modified_at'">
              {{ data.sort_order === 'asc' ? '↑' : '↓' }}
            </span>
          </th>
          <th scope="col">操作</th>
        </tr>
        </thead>

        <tbody>
        <tr v-for="item in filteredData" :key="item.id"
            style="vertical-align: middle">

          <td style="text-align: center;">
            <input type="checkbox" v-model="data.checked_items[ item.id]">
          </td>
          <td style="text-align: center;">{{ item.sort_number || '' }}</td>
          <td class="td-truncate" :title="item.article_title">
            <div class="td-truncate-inner">{{ item.article_title || '' }}</div>
          </td>
          <td style="text-align: center;">
            <label class="gen-type-badge"
                   :style="{ backgroundColor: generate_type_color[item.generate_type_id] || '#6c757d' }"
                   :title="labelGenerateTypeForRow(item.generate_type_id)">
              {{ labelGenerateTypeForRow(item.generate_type_id) }}
            </label>
          </td>
          <td class="td-truncate">
            <a v-if="item.posted_url" :href="item.posted_url" target="_blank"
               :title="item.posted_url">
              {{ item.posted_url }}
            </a>
            <span v-else>-</span>
          </td>

          <td style="text-align: center;">
            <label class="custom-cell"
                   :style="{ backgroundColor: article_generate_status_color[ item.article_generate_status] }">
              {{ labelArticleGenerateStatus(item.article_generate_status) }}
            </label>
          </td>

          <td style="text-align: center;">
            <label class="custom-cell"
                   :style="{ backgroundColor: cms_post_status_color[ item.cms_post_status] }">
              {{ labelCmsPostStatus(item.cms_post_status) }}
            </label>
          </td>
          <td style="white-space: nowrap; font-size: 13px;">{{ datePipe(item.created_at, 'yyyy/MM/dd hh:mm') }}</td>
          <td style="white-space: nowrap; font-size: 13px;">{{ datePipe(item.modified_at, 'yyyy/MM/dd hh:mm') }}</td>
          <td style="text-align: center;">
            <div style="display: flex; flex-direction: column; gap: 3px; align-items: center;">
              <button class="btn btn-primary btn-sm" style="padding: 2px 10px; min-width: 48px; white-space: nowrap;"
                      @click="handleEdit( item.id)">
                {{ $t( "Articles.edit") }}</button>
              <button class="btn btn-danger btn-sm" style="padding: 2px 10px; min-width: 48px; white-space: nowrap;"
                      @click="handleDelete( item.id, item.article_title)">
                {{ $t( "Articles.delete") }}</button>
            </div>
          </td>

        </tr>
        </tbody>
      </table>

    </div>
    <div class="row align-items-center" style="margin-top: 8px;">
      <div class="col-auto" style="display: flex; align-items: center; gap: 8px;">
        <label style="font-size: 13px; color: #555; white-space: nowrap; margin: 0;">表示件数：</label>
        <select v-model="data.page_num" @change="handlePageNumChange"
                style="padding: 2px 8px; font-size: 13px; border: 1px solid #dee2e6; border-radius: 4px;">
          <option v-for="n in PAGE_NUM_OPTIONS" :key="n" :value="n">{{ n }}件</option>
        </select>
      </div>
      <div class="col">
        <paginate style="cursor: pointer; margin: 0;"
                  v-model="data.current_page"
                  :page-count="page_count"
                  :page-range="3"
                  :margin-pages="2"
                  :prev-text="'<'"
                  :next-text="'>'"
        >
        </paginate>
      </div>
    </div>
  </div>

</template>

<style scoped>
/* === URL/テキスト モードバッジ === */
.mode-badge {
  font-size: 10px;
  padding: 1px 7px;
  border-radius: 10px;
  color: #fff;
  font-weight: 600;
  white-space: nowrap;
}
.mode-url  { background: #0d6efd; }
.mode-text { background: #198754; }

.urls-hint {
  font-size: 10px;
  color: #999;
  margin-top: 3px;
}

.primary-source-result {
  padding: 8px 12px;
  margin-bottom: 6px;
  border-radius: 4px;
  border-left: 4px solid #ccc;
}
.result-primary {
  background-color: #f0faf0;
  border-left-color: #28a745;
}
.result-secondary {
  background-color: #fffbf0;
  border-left-color: #ffc107;
}
.result-unknown {
  background-color: #f8f9fa;
  border-left-color: #6c757d;
}

.tr-line {
  height: 50px;
}

.td-left {
  text-align: right;
  width: 170px;
  white-space: nowrap;
  padding-right: 12px;
  vertical-align: top;
  padding-top: 14px;
}

.td-right {
  text-align: left;
  width: 660px;
  padding: 6px 0;
  vertical-align: top;
}

.td-combo {
  margin-top: 2px;
  margin-left: 0;
  width: 300px;
}

.td-text {
  padding: 6px 8px;
  margin: 2px 0;
  width: 600px;
  box-sizing: border-box;
}

.modal-del {
  height: 800px;
  max-height: 1024px;
  width: 480px;

  display: flex;
  justify-content: center;
  align-items: center;

  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0);
  z-index: 1000; /* 他の要素より前面に */

  top: 100px;
  left: 100px;
  position: absolute;
  padding: 20px;
}

.modal-create {
  width: 960px;
  max-height: 90vh;
  padding: 20px;

  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);

  display: flex;
  justify-content: center;
  align-items: flex-start;
  overflow-y: auto;

  background-color: rgba(0, 0, 0, 0);
  z-index: 1000;
}


.modal-content {
  background-color: #fff;
  padding: 20px;
  border-radius: 5px;
  max-width: 1600px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
}

.custom-cell {
  display: inline-block;
  text-align: center;
  padding: 2px 0;
  color: white;
  border-radius: 10px;
  font-size: 12px;
  white-space: nowrap;
  width: 56px;  /* 固定幅でバッジサイズ統一 */
}

.gen-type-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: 100%;
  min-height: 48px;
  text-align: center;
  padding: 6px 8px;
  color: white;
  border-radius: 10px;
  font-size: 12px;
  line-height: 1.4;
  white-space: normal;
  word-break: break-word;
  overflow-wrap: anywhere;
}

.tags {
  position: relative;
  float: left;
  color: #000000;
  line-height: 28px;

  margin-left: 10px;
  margin-top: 30px;
  margin-bottom: 1px;
  /* margin: 0 4px 4px 0; */
  padding: 0 22px 0 10px;
}

.tags .content {
  line-height: 28px;
}

.tags .del {
  width: 22px;
  height: 28px;
  text-align: center;
  cursor: pointer;
  position: absolute;
  top: -1px;
  right: 0;
}

.tags {
  background-color: #e0e0e0;
  border: 1px solid rgb(54, 54, 54);
  cursor: default;
}

/* ===== articles table ===== */
.articles-table {
  table-layout: fixed;
  width: 100%;
  font-size: 14px;
}

.articles-table th,
.articles-table td {
  vertical-align: middle;
  overflow: hidden;
  padding-top: 6px;
  padding-bottom: 6px;
}

.articles-table th {
  white-space: nowrap;
  text-align: left;
  font-size: 13px;
}

/* テキスト省略セル（2行クランプ） */
.td-truncate {
  overflow: hidden;
}

.td-truncate-inner {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  white-space: normal;
  word-break: break-all;
  line-height: 1.4;
}

.td-truncate a {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  white-space: normal;
  word-break: break-all;
  line-height: 1.4;
  color: inherit;
  text-decoration: none;
}

/* 画像生成モード開關樣式 */
.switch {
  position: relative;
  display: inline-block;
  width: 50px;
  height: 24px;
  flex-shrink: 0;
  vertical-align: middle;
}

.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  transition: .4s;
}

.slider:before {
  position: absolute;
  content: "";
  height: 18px;
  width: 18px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  transition: .4s;
}

input:checked + .slider {
  background-color: #2196F3;
}

input:checked + .slider:before {
  transform: translateX(26px);
}

.slider.round {
  border-radius: 24px;
}

.slider.round:before {
  border-radius: 50%;
}

</style>